'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import React, { useEffect, useState } from 'react';

const Tooltip = dynamic(() => import('react-tooltip').then((mod) => mod.Tooltip));

import styles from '@/components/global/tooltip/tooltip.module.scss';

interface Props {
  ariaLabelClose?: string;
  ariaLabelOpen?: string;
  children: React.ReactNode;
  content: string;
  dark?: boolean;
  id: string;
  place?: string;
  wrap?: boolean;
  tabIndex?: number | null;
  hover?: boolean;
}

const Component = (props: Props): React.JSX.Element => {
  const { ariaLabelClose, ariaLabelOpen, children, content, id, place, wrap, tabIndex, hover } = props;
  const [isCloseable, setIsCloseable] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeydown = (event: any) => {
      if (event.key === 'Tab') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown, true);
    };
  }, [props]);

  const afterHide = () => {
    setIsCloseable(false);
    setIsOpen(false);
  };

  const afterShow = () => {
    setIsCloseable(true);
    setIsOpen(true);
  };

  const rest: { [key: string]: string } = {};
  if (place) {
    rest['place'] = place;
  }

  const handleMouseEnter = () => {
    if (hover) {
      setIsOpen(true);
      setIsCloseable(true);
    }
  };

  const handleMouseLeave = () => {
    if (hover) {
      setIsOpen(false);
      setIsCloseable(false);
    }
  };

  return (
    <div>
      <button
        aria-label={isOpen ? ariaLabelOpen || 'close more info' : ariaLabelClose || 'more info'}
        className={classNames({ [styles.button]: true, [styles.dark]: props.dark, [styles.review]: hover })}
        data-tooltip-id={id}
        onClick={() => {
          if (!hover) {
            setIsCloseable(!isCloseable);
          }
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        type="button"
        tabIndex={tabIndex !== null ? tabIndex : undefined}
      >
        {children}
      </button>
      <div className="react-tooltip-container" style={{ zIndex: 1 }}>
        <span className={styles.status} data-place={place || 'none'} role="status">
          <Tooltip
            afterHide={afterHide}
            afterShow={afterShow}
            className={classNames({ tooltip: true, wrap: wrap })}
            closeOnEsc
            closeOnResize
            closeOnScroll
            content={content}
            id={id}
            isOpen={isOpen && isCloseable}
            opacity={1.0}
            openOnClick
            setIsOpen={setIsOpen}
            wrapper="span"
            {...rest}
          />
        </span>
      </div>
    </div>
  );
};

export default Component;
